import FetchWrapper from '../Common/Wrappers/FetchWrapper';
import { Service } from '../Common/service.enum';
import Logging from '../../Logging/Logging';
import {
  filtersToStandardQueryParams,
  sortModelToStandardQueryParam,
} from '../Common/filterSorting';
import {
  CustomerSummaryCollection,
  CustomerStatus,
  CustomerReplaceStatus,
  ContactCollection,
  ContactEmailSubscribedUpdate,
  Contact,
  Customer,
  CustomerReplaceExcludeFromSync,
} from '../../..';

export default class CustomerRepo {
  private fetch: FetchWrapper;

  constructor(fetch: FetchWrapper) {
    this.fetch = fetch;
  }

  getCustomersV41(
    startRow: number,
    endRow: number,
    filterModel: string[] = null,
    sortModel: string[] = null,
    customFilter: string = null,
  ): Promise<CustomerSummaryCollection> {
    const updatedSortModel: string[] = sortModel;

    // Detect a Status Sort.  Customers UI show Active / Inactive instead of the Approved from service
    if ((sortModel && sortModel[0].toLocaleLowerCase().includes('status'))) {
      // Also Sort by End date to insure the Approved (Active / Inactive) customers are sorted properly
      if (sortModel[0].toLocaleLowerCase().includes('asc')) {
        // End Date Ascending
        updatedSortModel.push('endDate desc');
      } else {
        // End Date Descending
        updatedSortModel.push('endDate asc');
      }
    }

    const apiServiceEndpoint = 'portal/v4.1/customers';
    const filterQueryParams = customFilter || filtersToStandardQueryParams(filterModel);
    const sortQueryParam = sortModelToStandardQueryParam(updatedSortModel);
    const numberRows = endRow > startRow ? endRow - startRow : 0;
    const pagingQueryParam = `&$top=${numberRows}&$skip=${startRow}&$count=true`;

    let queryParams = '';
    if (filterQueryParams && sortQueryParam) {
      queryParams = `?${filterQueryParams}&${sortQueryParam}`;
    } else if (filterQueryParams) {
      queryParams = `?${filterQueryParams}`;
    } else if (sortQueryParam) {
      queryParams = `?${sortQueryParam}`;
    }

    // Add the Paging Parameters
    if (pagingQueryParam) {
      queryParams += queryParams === '' ? `?${pagingQueryParam}` : pagingQueryParam;
    }

    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('GET', url);
    return this.fetch.getFromService<CustomerSummaryCollection>(Service.ECM, url);
  }

  getCustomerV41(customerUuid: string): Promise<Customer> {
    const apiServiceEndpoint = `portal/v4.1/customers/${customerUuid}`;

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('GET', url);
    return this.fetch.getFromService<Customer>(Service.ECM, url);
  }

  getCustomerContactsV41(customerUuid: string): Promise<ContactCollection> {
    const apiServiceEndpoint = `portal/v4.1/customers/${customerUuid}/contacts`;

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('GET', url);
    return this.fetch.getFromService<ContactCollection>(Service.ECM, url);
  }

  getCustomersContactsV41(
    customerUuid: string,
    startRow: number,
    endRow: number,
    filterModel: string[] = null,
    sortModel: string[] = null,
  ): Promise<CustomerSummaryCollection> {
    // FIX ME.  This API needs to support Filtering & Sorting.  Also needs to return Paging data and in "aodata", "value" format
    const apiServiceEndpoint = `portal/v4.1/customers/${customerUuid}/contacts`;
    const filterQueryParams = filtersToStandardQueryParams(filterModel);
    const sortQueryParam = sortModelToStandardQueryParam(sortModel);
    const numberRows = endRow > startRow ? endRow - startRow : 0;
    const pagingQueryParam = `&$top=${numberRows}&$skip=${startRow}&$count=true`;

    let queryParams = '';
    if (filterQueryParams && sortQueryParam) {
      queryParams = `?${filterQueryParams}&${sortQueryParam}`;
    } else if (filterQueryParams) {
      queryParams = `?${filterQueryParams}`;
    } else if (sortQueryParam) {
      queryParams = `?${sortQueryParam}`;
    }

    // Add the Paging Parameters
    if (pagingQueryParam) {
      queryParams += queryParams === '' ? `?${pagingQueryParam}` : pagingQueryParam;
    }

    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('GET', url);
    return this.fetch.getFromService<CustomerSummaryCollection>(Service.ECM, url);
  }

  updateCustomerContactV41(customerUuid: string, contactUuid: string, emailSubscribedInd: boolean): Promise<void> {
    const apiServiceEndpoint = `portal/v4.1/customers/${customerUuid}/contacts/${contactUuid}`;

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    const request: ContactEmailSubscribedUpdate [] = [];

    // Update the Contact
    const contactUpdate: ContactEmailSubscribedUpdate = {
      op: 'replace',
      path: '/emailSubscribedInd',
      value: emailSubscribedInd,
    };
    request.push(contactUpdate);

    Logging.Info('PATCH', url);
    return this.fetch.patchToService<void>(Service.ECM, url, request);
  }

  approveCustomerV4(customerUuid: string): Promise<void> {
    const apiServiceEndpoint = `portal/v4/customers/${customerUuid}`;

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    const request: CustomerReplaceStatus [] = [];

    // Replace the Status of the Customer
    const customerReplaceStatus: CustomerReplaceStatus = {
      op: 'replace',
      path: '/status',
      value: CustomerStatus.Approved,
    };
    request.push(customerReplaceStatus);

    Logging.Info('PATCH', url, request);
    return this.fetch.patchToService<void>(Service.ECM, url, request);
  }

  validateParentCustomerV4(
    parentCustomerCode: string,
    taxPayerCode: string,
    taxPayerDepartment: string,
    taxPayerDivision: string,
    taxPayerPartition: string,
  ): Promise<CustomerSummaryCollection> {
    const apiServiceEndpoint = 'portal/v4.1/customers';

    // Create the Filter Query
    let filterQueryParams = `$filter=${encodeURIComponent(`customercode eq:cs '${parentCustomerCode}'`)}`;

    // Add the Optional Taxpayer Fields
    filterQueryParams += taxPayerCode ? encodeURIComponent(` and taxpayerCode eq:cs '${taxPayerCode}'`) : '';
    filterQueryParams += taxPayerDepartment ? encodeURIComponent(` and department eq:cs '${taxPayerDepartment}'`) : '';
    filterQueryParams += taxPayerDivision ? encodeURIComponent(` and division eq:cs '${taxPayerDivision}'`) : '';
    filterQueryParams += taxPayerPartition ? encodeURIComponent(` and partition eq:cs '${taxPayerPartition}'`) : '';

    const pagingQueryParam = '&$top=1000&$skip=0&$count=true';
    const queryParams = `?${filterQueryParams}${pagingQueryParam}`;

    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('GET', url);
    return this.fetch.getFromService<CustomerSummaryCollection>(Service.ECM, url);
  }

  addCustomerV41(customer: Customer): Promise<Headers> {
    const apiServiceEndpoint = 'portal/v4.1/customers';

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('POST', url, customer);
    // Return the HTTP Headers instead of the Body.  The newly inserted Customer Uuuid will be in the 'Location' Header
    return this.fetch.postToServiceReturnHeaders<void>(Service.ECM, url, customer);
  }

  editCustomerV41(customer: Customer): Promise<void> {
    const apiServiceEndpoint = `portal/v4.1/customers/${customer.uuid}`;

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('PUT', url, customer);
    return this.fetch.putToService<void>(Service.ECM, url, customer);
  }

  addCustomerContactV41(customerUuid: string, contact: Contact): Promise<Headers> {
    const apiServiceEndpoint = `portal/v4.1/customers/${customerUuid}/contacts`;

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('POST', url, customerUuid, contact);
    // Return the HTTP Headers instead of the Body.  The newly inserted Contact Uuuid will be in the 'Location' Header
    return this.fetch.postToServiceReturnHeaders<void>(Service.ECM, url, contact);
  }

  excludeFromSyncCustomerV41(customerUuid: string, excludeFromSync: boolean): Promise<void> {
    const apiServiceEndpoint = `portal/v4.1/customers/${customerUuid}`;

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    const request: CustomerReplaceExcludeFromSync [] = [];

    // Replace the Exclude from Sync of the Customer
    const customerReplaceExcludeFromSync: CustomerReplaceExcludeFromSync = {
      op: 'replace',
      path: '/excludeFromSync',
      value: excludeFromSync,
    };
    request.push(customerReplaceExcludeFromSync);

    Logging.Info('PATCH', url, request);
    return this.fetch.patchToService<void>(Service.ECM, url, request);
  }
}
